/** Ekran „Przegląd" (Faza 6).
 *
 * Sam przegląd toczy się w chacie - ten ekran tylko go otwiera, pokazuje pasek
 * postępu kroków i pozwala wrócić albo przerwać.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SPACE_LABELS, type SpaceFilter } from "../app/store";
import { useAbandonReview, useReview, useStartReview } from "./hooks";
import { COUNTER_LABELS, type ReviewStats, type ReviewStepProgress } from "./types";

const SCOPES: SpaceFilter[] = ["all", "praca", "prywatne"];

function formatMoment(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
}

function counterLine(counts: Record<string, number>): string {
  const parts = Object.entries(counts)
    .filter(([, value]) => value > 0)
    .map(([key, value]) => `${COUNTER_LABELS[key] ?? key}: ${value}`);
  return parts.length ? parts.join(", ") : "pusto";
}

export function ReviewSteps({ steps }: { steps: ReviewStepProgress[] }) {
  return (
    <ol className="review__steps">
      {steps.map((step) => (
        <li key={step.key} className={`review__step review__step--${step.status}`}>
          <span className="review__step-number">{step.number}</span>
          <span className="review__step-title">{step.title}</span>
        </li>
      ))}
    </ol>
  );
}

export function ReviewStatsList({ stats }: { stats: ReviewStats }) {
  return (
    <dl className="review__stats">
      <div>
        <dt>na starcie</dt>
        <dd>{counterLine(stats.start)}</dd>
      </div>
      <div>
        <dt>na koniec</dt>
        <dd>{counterLine(stats.end)}</dd>
      </div>
      <div>
        <dt>kroki</dt>
        <dd>
          {stats.steps_done}
          {stats.steps_skipped.length > 0 && ` (pominięte: ${stats.steps_skipped.length})`}
        </dd>
      </div>
      {stats.duration_min !== null && (
        <div>
          <dt>czas</dt>
          <dd>{stats.duration_min} min</dd>
        </div>
      )}
    </dl>
  );
}

export function ReviewPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useReview();
  const start = useStartReview();
  const abandon = useAbandonReview();
  const [scope, setScope] = useState<SpaceFilter>("all");

  if (isLoading) return <p className="muted view">Wczytuję stan przeglądu…</p>;
  if (error) return <p className="error view">Nie udało się wczytać przeglądu.</p>;
  if (!data) return null;

  const session = data.session;

  return (
    <div className="view review">
      {session ? (
        <section className="card">
          <div className="card__head">
            <h2 className="card__title">
              Przegląd w toku — krok {session.step_number}/{session.step_count}
            </h2>
            <span className="badge">{session.space_scope ?? "wszystko"}</span>
          </div>

          <p className="muted">
            {session.step_title}
            {session.cursor.total > 0 &&
              ` — pozycja ${Math.min(session.cursor.index + 1, session.cursor.total)}/${
                session.cursor.total
              }`}
          </p>
          {session.cursor.current && (
            <p className="review__current">{session.cursor.current.title}</p>
          )}

          <ReviewSteps steps={session.steps} />

          <p className="muted">Rozpoczęty: {formatMoment(session.started_at)}</p>

          <div className="review__actions">
            <button className="btn btn--primary" onClick={() => navigate("/chat")}>
              Kontynuuj w chacie
            </button>
            <button
              className="btn btn--danger"
              disabled={abandon.isPending}
              onClick={() => abandon.mutate()}
            >
              {abandon.isPending ? "…" : "Przerwij"}
            </button>
          </div>
          {abandon.error && <p className="error">Nie udało się przerwać przeglądu.</p>}
        </section>
      ) : (
        <section className="card">
          <div className="card__head">
            <h2 className="card__title">Rozpocznij przegląd</h2>
          </div>
          <p className="muted">
            Najpierw oczyścimy głowę i inbox, potem zaktualizujemy listy i projekty, a na koniec
            zostanie miejsce na nowe pomysły. Przegląd toczy się w chacie — można go przerwać i
            wrócić.
          </p>

          <div className="review__scope" role="group" aria-label="Zakres przeglądu">
            {SCOPES.map((value) => (
              <button
                key={value}
                className={`chip ${scope === value ? "chip--active" : ""}`}
                aria-pressed={scope === value}
                onClick={() => setScope(value)}
              >
                {SPACE_LABELS[value]}
              </button>
            ))}
          </div>

          <ol className="review__steps">
            {data.steps.map((step) => (
              <li key={step.key} className="review__step review__step--todo">
                <span className="review__step-number">{step.number}</span>
                <span className="review__step-title">{step.title}</span>
              </li>
            ))}
          </ol>

          <button
            className="btn btn--primary btn--block"
            disabled={start.isPending}
            onClick={() =>
              start.mutate(scope === "all" ? null : scope, {
                onSuccess: () => navigate("/chat"),
              })
            }
          >
            {start.isPending ? "Zaczynam…" : "Rozpocznij przegląd"}
          </button>
          {start.error && <p className="error">Nie udało się rozpocząć przeglądu.</p>}

          {data.last_finished && (
            <div className="review__last">
              <h3 className="review__last-title">
                Ostatni ukończony: {formatMoment(data.last_finished.finished_at)}
                {data.last_finished.space_scope ? ` (${data.last_finished.space_scope})` : ""}
              </h3>
              <ReviewStatsList stats={data.last_finished.stats} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
